import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CText } from '../../components/common';

interface PeerComparisonProps {
  peerData?: Array<Record<string, string>>;
  currentCompany?: string;
  colors: any;
}

export const PeerComparison: React.FC<PeerComparisonProps> = ({ peerData, currentCompany, colors }) => {
  const navigate = useNavigate();

  // Defensive: ensure peerData is an array of plain objects
  const validPeerData = Array.isArray(peerData)
    ? peerData.filter((item): item is Record<string, string> => item != null && typeof item === 'object' && !Array.isArray(item))
    : [];

  if (validPeerData.length === 0) {
    return (
      <div className="text-center py-8">
        <CText type="M14" color={colors.grayScale500}>No peer comparison data available</CText>
      </div>
    );
  }

  const headers = Object.keys(validPeerData[0]);
  const nameKey = headers[0]; // First column is usually the company name

  // Identify numeric columns for color coding
  const numericHeaders = headers.filter(h => {
    const lc = h.toLowerCase();
    return lc.includes('p/e') || lc.includes('roe') || lc.includes('roce') ||
      lc.includes('div') || lc.includes('profit') || lc.includes('growth') ||
      lc.includes('npm') || lc.includes('opm');
  });

  const handlePeerClick = (peerName: string) => {
    if (peerName && peerName !== currentCompany) {
      navigate(`/company/${encodeURIComponent(peerName)}`);
    }
  };

  return (
    <div>
      <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>
        Peer Comparison
      </CText>
      <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${colors.borderColor}` }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: colors.primary }}>
              {headers.map((header, i) => (
                <th
                  key={header}
                  className={`px-3 py-2 text-left text-white font-medium whitespace-nowrap ${i === 0 ? 'sticky left-0 z-10' : ''}`}
                  style={i === 0 ? { backgroundColor: colors.primary, minWidth: 150 } : undefined}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {validPeerData.map((row, idx) => {
              const isCurrentCompany = currentCompany && row[nameKey]?.toLowerCase().includes(currentCompany.toLowerCase());
              return (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: isCurrentCompany
                      ? colors.primary + '10'
                      : idx % 2 === 0
                        ? colors.backgroundColor
                        : colors.inputBackground,
                    borderBottom: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {headers.map((header, i) => {
                    const val = row[header] || '-';
                    const isName = i === 0;
                    const isNumeric = numericHeaders.includes(header);
                    const numVal = isNumeric ? parseFloat(val.replace(/,/g, '').replace(/%/g, '')) : NaN;

                    let textColor = colors.textColor;
                    if (isNumeric && !isNaN(numVal)) {
                      if (header.toLowerCase().includes('p/e') && numVal > 40) textColor = '#f59e0b';
                      else if (numVal > 15 && (header.toLowerCase().includes('roe') || header.toLowerCase().includes('roce'))) textColor = '#22c55e';
                    }

                    return (
                      <td
                        key={header}
                        className={`px-3 py-2 whitespace-nowrap ${i === 0 ? 'sticky left-0 z-10' : ''}`}
                        style={{
                          backgroundColor: i === 0
                            ? isCurrentCompany
                              ? colors.primary + '10'
                              : idx % 2 === 0
                                ? colors.backgroundColor
                                : colors.inputBackground
                            : undefined,
                        }}
                      >
                        {isName ? (
                          <button
                            onClick={() => handlePeerClick(val)}
                            className="text-left hover:underline"
                            style={{
                              color: isCurrentCompany ? colors.primary : colors.textColor,
                              fontWeight: isCurrentCompany ? 600 : 400,
                            }}
                          >
                            <CText type={isCurrentCompany ? 'M12' : 'r12'} color={isCurrentCompany ? colors.primary : colors.textColor}>
                              {val} {isCurrentCompany && '(You)'}
                            </CText>
                          </button>
                        ) : (
                          <CText type="r12" color={textColor}>{val}</CText>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CText type="r10" color={colors.grayScale400} style={{ marginTop: 8 }}>
        Click on a peer company to view its details
      </CText>
    </div>
  );
};
